import React, { useState, useEffect, memo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useTheme, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ListItemButton from '@mui/material/ListItemButton';
import Collapse from '@mui/material/Collapse';

import ChevronRight from 'mdi-material-ui/ChevronRight';

import Translations from 'layouts/components/Translations';
import themeConfig from 'configs/themeConfig';
import { hasActiveChild } from '@core/layouts/utils';
import SubMenuItems from 'pages/components/SubMenuItems';

const MenuNavLink = styled(ListItemButton)(({ theme }) => ({
  borderRadius: 8,
  marginLeft: '10px',
  marginRight: '10px',
  marginTop: '5px',
  marginBottom: '5px',
  flexDirection: 'row',
  alignItems: 'center',
  listStyle: 'none',
  padding: '10px',
  textDecoration: 'none',
  transition: 'padding .25s ease-in-out',
  '&.active': {
    '&, &:hover': {
      backgroundColor: theme.palette.primary.light,
    },
    '& .MuiTypography-root': {
      fontWeight: 500,
      color: `${theme.palette.common.white} !important`,
    },
    '& .MuiListItemIcon-root': {
      color: `${theme.palette.common.white} !important`,
    },
  },
}));

const ParentDiv = styled(Box)(() => ({
  top: 0,
  flexDirection: 'row',
  display: 'flex',
  width: '100%',
  transition: 'opacity .25s ease-in-out',
}));

const MenuItemTextMetaWrapper = styled(Box)({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  transition: 'opacity .25s ease-in-out',
  ...(themeConfig.menuTextTruncate && { overflow: 'hidden' }),
});

const MenuGroupToggleRightIcon = styled(ChevronRight)(({ theme }) => ({
  color: theme.palette.text.secondary,
  transition: 'transform .25s ease-in-out',
}));

const SideNavbarItemlist = ({
  item,
  navVisible,
  activePath,
  navHover,
  navigationBorderWidth,
  toggleNavVisibility,
  settings,
}) => {
  const [subnav, setSubNav] = useState(false);
  const theme = useTheme();
  const { navCollapsed, skin } = settings;

  const location = useLocation();
  const currentURL = location.pathname;
  const { collapsedNavWidth } = themeConfig;
  const menuGroupCollapsedStyles =
    navCollapsed && !navHover ? { opacity: 0 } : { opacity: 1 };

  useEffect(() => {
    if (hasActiveChild(item, currentURL)) {
      setSubNav(true);
    }
  }, [currentURL, item, navCollapsed, navHover]);

  const conditionalArrowIconColor = () => {
    if (skin === 'semi-dark' && theme.palette.mode === 'light') {
      return {
        color: `rgba(${theme.palette.customColors.dark}, 0.68)`,
      };
    } else if (skin === 'semi-dark' && theme.palette.mode === 'dark') {
      return {
        color: `rgba(${theme.palette.customColors.light}, 0.68)`,
      };
    } else return {};
  };

  return (
    <div key={`nav_${item.title}`}>
      <Link
        to={`${item.path}`}
        style={{ textDecoration: 'none', backgroundColor: 'lightpink' }}
      >
        <MenuNavLink
          item={item}
          className={
            location.pathname === item.path
              ? 'active'
              : item.types === 'sub' && subnav
              ? 'active'
              : ''
          }
          onClick={(e) => {
            if (item.subNav) {
              setSubNav(!subnav);
            }

            if (item.path === undefined) {
              e.preventDefault();
              e.stopPropagation();
            }

            if (navVisible && item.path) {
              toggleNavVisibility();
            }
          }}
          sx={{
            py: 2.25,
            pr:
              navCollapsed && !navHover
                ? (collapsedNavWidth - navigationBorderWidth - 24 - 16) / 8
                : 3,
            pl:
              navCollapsed && !navHover
                ? (collapsedNavWidth - navigationBorderWidth - 24 - 16) / 8
                : 4,
            color:
              item.path === activePath
                ? `${theme.palette.common.white} !important`
                : `${theme.palette.secondary.main} !important`,
            '&.active': {
              '&, &:hover': {
                backgroundColor:
                  item.types === 'sub' && subnav
                    ? `${theme.palette.divider}`
                    : theme.palette.primary.light,
              },
              '& .MuiTypography-root': {
                fontWeight: 500,
                color:
                  item.types === 'sub' && subnav
                    ? `${theme.palette.secondary.main} !important`
                    : `${theme.palette.common.white} !important`,
              },
              '& .MuiListItemIcon-root': {
                color:
                  item.types === 'sub' && subnav
                    ? `${theme.palette.secondary.main} !important`
                    : `${theme.palette.common.white} !important`,
              },
            },
          }}
        >
          <ParentDiv>
            {item.icon}
            <MenuItemTextMetaWrapper
              sx={{
                ...(navCollapsed && !navHover
                  ? { opacity: 0 }
                  : { opacity: 1 }),
              }}
            >
              <Typography
                {...((themeConfig.menuTextTruncate ||
                  (!themeConfig.menuTextTruncate && navCollapsed)) && {
                  noWrap: true,
                  marginLeft: '12px',
                  fontSize: '16px',
                  letterSpacing: '0.3px',
                })}
              >
                {<Translations text={item.title} />}
              </Typography>
              {item.subNav ? (
                <MenuGroupToggleRightIcon
                  sx={{
                    ...conditionalArrowIconColor(),
                    ...(item.types === 'sub' && subnav
                      ? { transform: 'rotate(90deg)' }
                      : {}),
                  }}
                />
              ) : null}
            </MenuItemTextMetaWrapper>
          </ParentDiv>
        </MenuNavLink>
      </Link>
      {subnav && (
        <Collapse
          in={subnav}
          timeout="auto"
          sx={{
            pl: 0,
            width: '100%',
            ...menuGroupCollapsedStyles,
            transition: 'all .25s ease-in-out',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {item.subNav.map((items, index) => {
            return (
              <SubMenuItems
                key={index}
                index={index}
                items={items}
                location={location}
                navCollapsed={navCollapsed}
                navHover={navHover}
                skin={skin}
                navVisible={navVisible}
                toggleNavVisibility={toggleNavVisibility}
              />
            );
          })}
        </Collapse>
      )}
    </div>
  );
};

export default memo(SideNavbarItemlist);
